import React, { useCallback, useMemo, useState } from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { RemnantsManageRow } from '../../../api/RemnantsManage';
import FormulaCell from '../../../RemnantsManage/FormulaCell';
import AvailableExplainCell from '../../../RemnantsManage/AvailableExplainCell';
import {
  ago,
  collectBoundWarehouses,
  isQuarantineActive,
  isWbMarketplace,
  mpLabel,
  num,
  quarantineLabel,
  rowMpView,
  shortWhName,
} from './fbsDisplayHelpers';

type Props = {
  rows: RemnantsManageRow[];
  isCombo: boolean;
  pending?: boolean;
  onRecalculated: () => void | Promise<void>;
};

export const GoodsFbsStockTable = ({
  rows: initialRows,
  isCombo,
  pending,
  onRecalculated,
}: Props) => {
  const [rows, setRows] = useState(initialRows);

  React.useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const boundWarehouses = useMemo(() => collectBoundWarehouses(rows), [rows]);

  const patchMpFormula = useCallback(
    (
      mpArticle: string,
      marketplace: 'wb' | 'ozon',
      mpWarehouseId: number,
      next: string | null,
    ) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.article !== mpArticle || r.mp_warehouse_id !== mpWarehouseId) {
            return r;
          }
          const key =
            marketplace === 'wb' ? 'wb_mp_formulas' : 'ozon_mp_formulas';
          const list = (r[key] || []).map((f) =>
            f.mp_warehouse_id === mpWarehouseId
              ? { ...f, share_formula: next }
              : f,
          );
          return { ...r, [key]: list };
        }),
      );
    },
    [],
  );

  const patchWarehouseComboShare = useCallback(
    (mpArticle: string, warehouseId: number, next: string | null) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.article !== mpArticle) return r;
          const list = (r.self_warehouses_1c || []).map((s) =>
            s.warehouse_id === warehouseId
              ? { ...s, share_in_combo: next }
              : s,
          );
          return { ...r, self_warehouses_1c: list };
        }),
      );
    },
    [],
  );

  const columns: ColumnsType<RemnantsManageRow> = useMemo(() => {
    const selfCols: ColumnsType<RemnantsManageRow> = boundWarehouses.flatMap(
      (w) => {
        const children: ColumnsType<RemnantsManageRow> = [
          {
            title: '1С',
            width: 64,
            render: (_, row) => {
              if (row.is_combo) return '';
              const slice = (row.self_warehouses_1c || []).find(
                (s) => s.warehouse_id === w.id,
              );
              if (!slice?.connected) return '—';
              return num(slice.remnant_1c);
            },
          },
          {
            title: 'резерв',
            width: 72,
            render: (_, row) => {
              if (row.is_combo) return '';
              const slice = (row.self_warehouses_1c || []).find(
                (s) => s.warehouse_id === w.id,
              );
              if (!slice?.connected) return '—';
              return num(slice.reserved_mp);
            },
          },
          {
            title: 'доступно',
            width: 96,
            render: (_, row) => {
              const slice = (row.self_warehouses_1c || []).find(
                (s) => s.warehouse_id === w.id,
              );
              if (!slice?.connected) return row.is_combo ? '' : '—';
              return (
                <AvailableExplainCell
                  article={row.article}
                  warehouseId={w.id}
                  value={num(slice.available_remnant)}
                />
              );
            },
          },
        ];
        if (!isCombo) {
          children.push({
            title: 'доля комбо',
            width: 120,
            render: (_, row) => {
              if (row.is_combo) return '';
              const slice = (row.self_warehouses_1c || []).find(
                (s) => s.warehouse_id === w.id,
              );
              if (!slice?.connected) return '—';
              return (
                <FormulaCell
                  article={row.article}
                  warehouseId={w.id}
                  value={slice.share_in_combo ?? row.share_combo}
                  onSaved={(next) =>
                    patchWarehouseComboShare(row.article, w.id, next)
                  }
                  onRecalculated={onRecalculated}
                />
              );
            },
          });
        }
        children.push({
          title: 'синхр.',
          width: 80,
          render: (_, row) => {
            if (row.is_combo) return '';
            const slice = (row.self_warehouses_1c || []).find(
              (s) => s.warehouse_id === w.id,
            );
            if (!slice?.connected) return '—';
            return ago(slice.remnant_synced_at);
          },
        });
        return [
          {
            title: shortWhName(w.name),
            key: `wh_${w.id}`,
            children,
          },
        ];
      },
    );

    return [
      {
        title: 'Склад МП',
        key: 'mp_warehouse',
        fixed: 'left' as const,
        width: 180,
        render: (_, row) => (
          <div>
            <span>
              {mpLabel(row.marketplace)} · {row.mp_warehouse_name || '—'}
            </span>
            {row.mp_is_active === false ? (
              <Tag style={{ marginLeft: 6 }}>выкл</Tag>
            ) : null}
          </div>
        ),
      },
      ...selfCols,
      {
        title: 'Доступно МП',
        dataIndex: 'available_remnant',
        width: 100,
        render: num,
      },
      {
        title: 'Формула МП',
        key: 'formula',
        width: 140,
        render: (_, row) => {
          const view = rowMpView(row, row.marketplace);
          const mpKey = isWbMarketplace(row.marketplace) ? 'wb' : 'ozon';
          const formula =
            row.mp_warehouse_id != null
              ? view.formulas.find(
                  (f) => f.mp_warehouse_id === row.mp_warehouse_id,
                )
              : view.formulas[0];
          if (!formula) return '—';
          return (
            <FormulaCell
              article={row.article}
              mpWarehouseId={formula.mp_warehouse_id}
              value={formula.share_formula}
              onSaved={(next) =>
                patchMpFormula(
                  row.article,
                  mpKey,
                  formula.mp_warehouse_id,
                  next,
                )
              }
              onRecalculated={onRecalculated}
            />
          );
        },
      },
      {
        title: 'Блок',
        width: 70,
        render: (_, row) => {
          const view = rowMpView(row, row.marketplace);
          return view.blocked ? <Tag color="red">блок</Tag> : '—';
        },
      },
      {
        title: 'Связь',
        width: 72,
        render: (_, row) =>
          row.mp_linked ? (
            <Tag color="green">есть</Tag>
          ) : (
            <Tag color="default">нет</Tag>
          ),
      },
      {
        title: 'Откл.',
        width: 72,
        render: (_, row) =>
          row.in_sale === false ? <Tag color="volcano">откл.</Tag> : '—',
      },
      {
        title: 'Карантин',
        width: 140,
        render: (_, row) => {
          const view = rowMpView(row, row.marketplace);
          return isQuarantineActive(view.quarantine_at) ? (
            <Tag color="orange">
              {quarantineLabel(view.quarantine_at, view.quarantine_reason)}
            </Tag>
          ) : (
            '—'
          );
        },
      },
      {
        title: 'Резерв МП',
        width: 80,
        render: (_, row) => num(rowMpView(row, row.marketplace).reserved),
      },
      {
        title: 'Отправлено',
        width: 80,
        render: (_, row) => num(rowMpView(row, row.marketplace).last_sent),
      },
      {
        title: 'На МП',
        width: 80,
        render: (_, row) => num(rowMpView(row, row.marketplace).mp_stock),
      },
      {
        title: 'К отправке',
        width: 100,
        render: (_, row) => {
          const view = rowMpView(row, row.marketplace);
          return (
            <span>
              {num(view.target)}
              {view.need_update ? (
                <Tag color="orange" style={{ marginLeft: 4 }}>
                  queue
                </Tag>
              ) : null}
            </span>
          );
        },
      },
    ];
  }, [
    boundWarehouses,
    isCombo,
    onRecalculated,
    patchMpFormula,
    patchWarehouseComboShare,
  ]);

  return (
    <Table
      size="small"
      loading={pending}
      pagination={false}
      scroll={{ x: 'max-content' }}
      rowKey={(r) =>
        String(r.row_key || `${r.mp_warehouse_id}-${r.marketplace}`)
      }
      locale={{ emptyText: 'Нет данных по складам маркетплейса' }}
      dataSource={rows}
      columns={columns}
    />
  );
};
